import { CircleAlert, CircleCheck } from 'lucide-react';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';

type CheckStatus = 'SUCCESS' | 'WARNING';

type Variants = 'COMPLETE' | 'PARTIAL' | 'NEW_INVOICE' | 'NO_DUPLICATE';

type InvoiceChecksCardProps = {
  variant: Variants;
};

export const InvoiceChecksCard = ({ variant }: InvoiceChecksCardProps) => {
  const checkResults = getCheckResults(variant);

  return (
    <Stack space={3} className="rounded-lg border-seapoint-gray-border bg-white p-4">
      {checkResults.map((r) => (
        <InvoiceCheckItem key={r.message} label={r.message} status={r.status} />
      ))}
    </Stack>
  );
};

type InvoiceCheckItemProps = {
  label: string;
  status: CheckStatus;
};

const InvoiceCheckItem = ({ label, status }: InvoiceCheckItemProps) => {
  const icon = getStatusIcon(status);

  return (
    <Inline space={2} wrap="nowrap" align="start" className="md:items-center">
      <div className="h-4 w-4 shrink-0 pt-1 md:pt-0">{icon}</div>

      <p className="text-sm text-black">{label}</p>
    </Inline>
  );
};

const getCheckResults = (variant: Variants): { status: CheckStatus; message: string }[] => {
  switch (variant) {
    case 'COMPLETE':
      return [
        {
          status: 'SUCCESS',
          message: `No duplicate detected - this invoice hasn't been paid yet`,
        },
        {
          status: 'SUCCESS',
          message: 'Known recipient - bank details match those previously paid',
        },
        {
          status: 'SUCCESS',
          message: 'Accounting ready - you have all the details needed for bookkeeping',
        },
      ];
    case 'PARTIAL':
      return [
        {
          status: 'SUCCESS',
          message: `No duplicate detected - this invoice hasn't been paid yet`,
        },
        {
          status: 'SUCCESS',
          message: 'Accounting ready - you have all the details needed for bookkeeping',
        },
        {
          status: 'WARNING',
          message: 'New details - different from past payments to this recipient',
        },
      ];
    case 'NEW_INVOICE':
      return [
        {
          status: 'SUCCESS',
          message: `It looks like this is a new invoice that you haven't paid before`,
        },
        {
          status: 'SUCCESS',
          message: 'All information required for reconciliation is present',
        },
      ];
    case 'NO_DUPLICATE':
      return [
        {
          status: 'SUCCESS',
          message: `No duplicate detected - this invoice hasn't been paid yet`,
        },
        {
          status: 'SUCCESS',
          message: 'Accounting ready - you have all the details needed for bookkeeping',
        },
      ];
    default:
      return exhaustiveCheck(variant);
  }
};

const getStatusIcon = (status: CheckStatus) => {
  const baseProps = { className: 'h-full w-full', color: '#3F9B21' };

  switch (status) {
    case 'SUCCESS':
      return <CircleCheck {...baseProps} />;
    case 'WARNING':
      return <CircleAlert {...baseProps} color="#FF883E" />;
    default:
      return exhaustiveCheck(status);
  }
};
